// © 2026 Orpyx Medical Technologies Inc.
import * as React from "react";
import {memo} from "react";
import {bindActionCreators, Dispatch} from "redux";
import {connect} from "react-redux";

import {PatientsListTableEvents, PatientsListTableMeta, PatientsListTableOwnProps, PatientsListTableProps} from "./patients-list-table.interface";
import {TableBody} from "@material-ui/core";
import {IOrpyxApplicationStore} from "../../../../../store/application.reducers";
import {PatientsListSelectors} from "../../store/patients-list.selectors";
import {OperationProgress, OperationSkeleton} from "orpyx-web-common";
import {PatientFilterType, IGetPatientsListResponse, PatientPriorityStatus} from "doctors-dashboard/http-clients/index";
import {PatientsListTableHeader} from "../patients-list-table-header/patients-list-table-header";
import {PatientsListTableItem} from "../patients-list-table-item/patients-list-table-item";
import {PATIENTS_LIST_SELECT_PATIENT} from "../../store/patients-list.actions";
import Table from "@material-ui/core/Table";
import {usePatientsListTableStyle} from "./patients-list-table.style";

const PatientsListTableTemplate = memo((props: PatientsListTableMeta) => {
    const classes = usePatientsListTableStyle();

    const canDisplayStatus = props.patientType === PatientFilterType.InactivePatients;
    const canDisplayPortalLink = props.patientType === PatientFilterType.NewPatients;
    const canDisplayOpenInPortalButton = props.patientType === PatientFilterType.InactivePatients;
    const canDisplayDaysSinceLastReview = props.patientType === PatientFilterType.Active;
    const canDisplayAllowAccessEndDate = props.patientType === PatientFilterType.InactivePatients;
    const canDisplayActionRequiredAlerts = props.patientType === PatientFilterType.Active;
    const canDisplayInsoleSerialNumbers = props.patientType === PatientFilterType.Active;
    const canDisplayDateOfPatientAuthorization = props.patientType !== PatientFilterType.NewPatients;
    const canDisplayLastStatusUpdatedAt = props.patientType === PatientFilterType.InactivePatients;

    if (props.loading || props.error) {
        return <>
            <OperationProgress inProcess={props.loading} error={props.error}/>
            <OperationSkeleton height={60} rows={8}/>
        </>;
    }

    const onPatientSelected = (patient: IGetPatientsListResponse | undefined) => {
        if (!patient) {
            return;
        }

        const isActive = props.patientType === PatientFilterType.Active;
        const isPaused = props.patientType === PatientFilterType.InactivePatients
            && patient.statusName === PatientPriorityStatus.Paused;

        if (isActive || isPaused) {
            props.onPatientSelected(patient);
        }
    };

    return (<Table id="patient-list-table">
            <PatientsListTableHeader
                classes={classes}
                canDisplayStatus={canDisplayStatus}
                canDisplayDaysSinceLastReview={canDisplayDaysSinceLastReview}
                canDisplayAllowAccessEndDate={canDisplayAllowAccessEndDate}
                canDisplayActionRequiredAlerts={canDisplayActionRequiredAlerts}
                canDisplayDateOfPatientAuthorization={canDisplayDateOfPatientAuthorization}
                canDisplayInsoleSerialNumbers={canDisplayInsoleSerialNumbers}
                canDisplayLastStatusUpdatedAt={canDisplayLastStatusUpdatedAt}
                canDisplayOpenInPortalButton={canDisplayOpenInPortalButton}
            />

            <TableBody>
                {props.patients.map(patient =>
                    <PatientsListTableItem
                        key={patient.patientId}
                        canDisplayStatus={canDisplayStatus}
                        canDisplayPortalLink={canDisplayPortalLink}
                        canDisplayDaysSinceLastReview={canDisplayDaysSinceLastReview}
                        canDisplayAllowAccessEndDate={canDisplayAllowAccessEndDate}
                        canDisplayActionRequiredAlerts={canDisplayActionRequiredAlerts}
                        canDisplayDateOfPatientAuthorization={canDisplayDateOfPatientAuthorization}
                        canDisplayInsoleSerialNumbers={canDisplayInsoleSerialNumbers}
                        canDisplayLastStatusUpdatedAt={canDisplayLastStatusUpdatedAt}
                        canDisplayOpenInPortalButton={canDisplayOpenInPortalButton}
                        onPatientSelected={onPatientSelected}
                        patient={patient}
                    />)}
            </TableBody>
        </Table>
    );
});

const mapStateToProps = (state: IOrpyxApplicationStore): PatientsListTableOwnProps => {
    return {
        patients: PatientsListSelectors.patients(state),

        loading: PatientsListSelectors.loading(state),
        error: PatientsListSelectors.error(state),
    };
};

const mapDispatchToProps = (dispatch: Dispatch, props: PatientsListTableProps): PatientsListTableEvents => {
    return bindActionCreators({
        onPatientSelected: PATIENTS_LIST_SELECT_PATIENT
    }, dispatch);
};

export const PatientsListTable = memo(connect(mapStateToProps, mapDispatchToProps)(PatientsListTableTemplate));

